/**
 * CellarEdge design tokens — the single source of truth for the UI's look.
 *
 * Why a TS token object (not Tailwind): Tailwind v4 isn't actually wired on this CRA app, and the
 * components style with inline `style={{}}` objects. These tokens let every component reference the
 * same palette/spacing/type, so the look is consistent and restylable from one place. The same values
 * are mirrored as CSS variables in index.css for anything that prefers CSS/className.
 *
 * Aesthetic: a premium "wine market" identity drawn from the CellarEdge logo — claret primary, warm
 * neutral surfaces, slate-warm ink, green/amber/red kept for their existing health-signal meanings.
 */

export const color = {
  // Claret — the brand wine-red. Primary actions, active nav, key accents.
  claret: {
    950: '#3E0E14',
    900: '#52131C',
    800: '#6B1B27',
    700: '#842331', // deep / hover
    600: '#9A2C3B', // PRIMARY
    500: '#B23E4E',
    300: '#D98C97',
    100: '#F3DEE2',
    50: '#FBF1F3', // tint surface
  },

  // Warm-tinted ink for text.
  ink: {
    900: '#211C1A', // strong headings
    700: '#3F3835', // body strong
    600: '#574E4A', // body
    500: '#7A6F6A', // muted
    400: '#9C928D', // subtle / placeholder text
  },

  // Warm neutrals for surfaces, borders, backgrounds.
  surface: '#FFFFFF',
  canvas: '#FBF8F5', // warm off-white page background
  border: '#ECE4DD',
  borderStrong: '#DBD0C7',
  hover: '#F6F0EA',

  // Semantic — meanings preserved from the existing health UI.
  success: { fg: '#2E7D46', bg: '#E9F3EC', border: '#C4E2CD' },
  warning: { fg: '#B26A1B', bg: '#FBF0E1', border: '#F1DCBC' },
  danger: { fg: '#B23B2E', bg: '#FBEAE7', border: '#F2C9C2' },
  info: { fg: '#9A2C3B', bg: '#FBF1F3', border: '#F3DEE2' }, // claret-tinted "info"

  // Muted gold — sparing premium accent.
  gold: '#B0883E',
} as const;

export const font = {
  sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  // Editorial serif for the brand wordmark + major display headings.
  serif: "Georgia, 'Times New Roman', 'Iowan Old Style', serif",
  mono: "'SF Mono', 'Source Code Pro', Menlo, Monaco, Consolas, monospace",
} as const;

export const radius = {
  sm: '6px',
  md: '10px',
  lg: '14px',
  pill: '9999px',
} as const;

export const shadow = {
  sm: '0 1px 2px rgba(46, 24, 16, 0.06)',
  md: '0 4px 16px rgba(46, 24, 16, 0.08)',
  lg: '0 12px 32px rgba(46, 24, 16, 0.12)',
} as const;

export const space = {
  xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px', xxl: '32px',
} as const;

export const tokens = { color, font, radius, shadow, space };
export default tokens;
